import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { FileText, Search, Eye, Calendar, User, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import CategoryFilter from '../components/CategoryFilter';

const ArticlesPage = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadArticles(); }, []);

  const loadArticles = async () => {
    try {
      const res = await api.get('/articles');
      setArticles(res.data);
      setCategories([...new Set(res.data.map((a) => a.category).filter(Boolean))]);
    } catch {
      toast.error('فشل تحميل المقالات');
    } finally {
      setLoading(false);
    }
  };

  const filtered = articles.filter((a) => {
    const matchCat = !selectedCategory || a.category === selectedCategory;
    const matchSearch = !searchQuery || a.title.includes(searchQuery) || a.excerpt?.includes(searchQuery);
    return matchCat && matchSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="articles-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-accent mb-2">المقالات</h1>
        <p className="text-muted-foreground">مقالات متنوعة في عالم القراءة والمعرفة</p>
      </div>

      {/* Search */}
      <div className="max-w-xl mx-auto">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="ابحث في المقالات..."
            className="pr-10 h-11"
            data-testid="articles-search"
          />
        </div>
      </div>

      {/* Filters */}
      {categories.length > 0 && (
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      )}

      {/* Articles Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-20">
          <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">لا توجد مقالات</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {filtered.map((article) => (
            <div
              key={article.id}
              onClick={() => navigate(`/articles/${article.id}`)}
              className="group cursor-pointer rounded-xl overflow-hidden bg-white border border-border/50 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
              data-testid={`article-card-${article.id}`}
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={article.image || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?auto=format&fit=crop&q=80&w=800'}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {article.category && (
                  <span className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-accent text-xs font-semibold px-2.5 py-1 rounded-md">
                    {article.category}
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-bold text-accent text-base leading-snug mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                {article.excerpt && (
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                )}
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border/50">
                  <div className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    <span className="truncate max-w-[120px]">{article.author}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {article.views > 0 && (
                      <div className="flex items-center gap-1">
                        <Eye className="w-3.5 h-3.5" />
                        <span>{article.views}</span>
                      </div>
                    )}
                    {(article.comment_count > 0) && (
                      <div className="flex items-center gap-1 text-emerald-600">
                        <MessageCircle className="w-3.5 h-3.5" />
                        <span>{article.comment_count}</span>
                      </div>
                    )}
                    {article.date && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>{new Date(article.date).toLocaleDateString('ar', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ArticlesPage;
